import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useUpdateShopWorkflowLogistician from "@babackapp/private-api-client/client/use-update-shop-workflow-logistician";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import useTranslation from "@babackapp/translations/use-translation";
import { Form, FormField } from "@babackapp/ui/components/form";
import { toast } from "@babackapp/ui/components/sonner";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";

type FormValues = {
  canValidateAfterMerchantValidation: boolean;
};

/**
 * BAB-2943. Separate form from LogisticianOptionsForm on purpose: this setting
 * lives on `workflowLogistician`, not on `preferences`, so it goes through the
 * workflowlogistician endpoint.
 *
 * The switch is locked while automatic processing is on — the backend refuses
 * the combination (409) because a late logistician validation would re-emit
 * LOGISTICIAN_APPROVE and have the auto-treatment refund units the merchant
 * already refunded.
 */
export function CanValidateAfterMerchantValidationForm() {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const isAdmin = useViewerHasAtLeastAdminRights();
  const { mutateAsync: updateWorkflowLogistician } =
    useUpdateShopWorkflowLogistician(shop.id);

  const isAutoTreatActivated =
    !!shop.preferences.autoTreateOnLogisticianValidate;

  const form = useForm<FormValues>({
    defaultValues: {
      canValidateAfterMerchantValidation:
        shop.workflowLogistician.canValidateAfterMerchantValidation === true,
    },
  });

  const values = useWatch({ control: form.control });

  const onSubmit = form.handleSubmit(
    async ({ canValidateAfterMerchantValidation }) => {
      try {
        await updateWorkflowLogistician({
          activated: shop.workflowLogistician.activated,
          canValidateAfterMerchantValidation,
        });
        form.reset({ canValidateAfterMerchantValidation });
      } catch {
        toast.error(
          t(
            "settings.workflowLogistician.canValidateAfterMerchantValidation.disabledBecauseAutoTreat",
          ),
        );
        form.reset({
          canValidateAfterMerchantValidation:
            shop.workflowLogistician.canValidateAfterMerchantValidation ===
            true,
        });
      }
    },
  );

  useEffect(() => {
    if (form.formState.isDirty) onSubmit();
  }, [values, form.formState.isDirty]);

  const disabledReason = !isAdmin
    ? t("settings.shop.errors.notAdmin")
    : isAutoTreatActivated
      ? t(
          "settings.workflowLogistician.canValidateAfterMerchantValidation.disabledBecauseAutoTreat",
        )
      : false;

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <FormField
          control={form.control}
          name="canValidateAfterMerchantValidation"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.workflowLogistician.canValidateAfterMerchantValidation.title",
              )}
              description={t(
                "settings.workflowLogistician.canValidateAfterMerchantValidation.description",
              )}
              {...field}
              disabled={disabledReason}
            />
          )}
        />
      </form>
    </Form>
  );
}
